import React from 'react';
import { useLoaderData } from 'react-router';
import { useAppDisplayModeContext } from '@archipro-design/aria';
import { Button, useStyles, Flex } from '@archipro-design/aria';
import * as S from './QuantityGroup.style';
import { CustomShoppingCart } from '@archipro-design/icons';
import { useVariantSelectionState } from '@modules/product/context/VariantSelectionContext';
import { useVariantStock } from '../../hook/use-variant-stock';
import { useSampleStock } from '../../hook/use-sample-stock';
import ShippingPopup from '@modules/ecommerce/component/shipping-popup/ShippingPopup';
import { useAddToCart } from '@modules/ecommerce/hook/use-add-to-cart';
import LastAddedPopup from '~/modules/ecommerce/component/shopping-cart-popup/LastAddedPopup.mobile';
import type { CartItem } from '~/modules/ecommerce/type/cart-item';
import type { ItemShippingType } from '~/modules/ecommerce/context/ItemShippingContext';
import { useTracker } from '@archipro-website/tracker';
import { useCartPopupState } from '~/modules/ecommerce/context/CartPopupContext';
import { useToaster } from '~/modules/root/context/ToasterContext';
import type { ProductPageLoaderData } from '~/routes/_app.product.$urlSegment';
import { useVariantShipping } from '../../hook/use-variant-shipping';
import { fetcherIsDone } from '~/utils/fetcherHelper';
import PurchaseButtonGroup from '@modules/product/component/variant-purchase/PurchaseButtonGroup';
import { WhereToBuyButton } from '../product-details/WhereToBuyButton';

const QuantityGroup = ({
    whereToBuyEnabled,
    onClickWhereToBuy,
}: {
    whereToBuyEnabled?: boolean;
    onClickWhereToBuy?: () => void;
}) => {
    const { mobile } = useAppDisplayModeContext();

    const { selectedVariant } = useVariantSelectionState();
    const { canAddToCart } = useVariantStock();
    const { product, shoppingCartSummary } =
        useLoaderData<ProductPageLoaderData>();

    const { canPurchase: sampleCanPurchase, priceText: samplePrice } =
        useSampleStock(selectedVariant);

    const [addSampleToCart, sampleFetcher] = useAddToCart(true);
    const [lastItemAdded, setLastItemAdded] = React.useState<CartItem | null>(
        null,
    );

    const [modalOpen, setModalOpen] = React.useState(false);
    const { open: cartPopupOpen, onChange: onCartPopupChange } =
        useCartPopupState();

    const { shippingMethods: sampleShippingMethods } = useVariantShipping(true);

    const tracker = useTracker();
    const { toast } = useToaster();
    const { css } = useStyles({ canAddToCart });

    const onSampleShippingConfirm = (
        shippingMethod: ItemShippingType | null,
    ) => {
        tracker.log('AddToCartSample', {
            url: new URL(window.location.href),
            targetTracker: 'archiproTracker',
            data: {
                // format: VariantID,CartID
                ExtraData: `${selectedVariant?.ID || 0},${
                    shoppingCartSummary?.ID || 0
                }`,
            },
        });
        if (!selectedVariant) return;
        addSampleToCart(selectedVariant, 1, shippingMethod);
    };

    const pickupLocations = product.Professional.PickupLocations;

    const hasSampleShippingOptions =
        sampleShippingMethods.length > 0 || pickupLocations.length > 0;

    React.useEffect(() => {
        //WHATS GOING ON HERE?
        //TECH DEBT
        if (fetcherIsDone(sampleFetcher) && sampleFetcher.data) {
            if ('error' in sampleFetcher.data) {
                toast(sampleFetcher.data.message || 'Unable to add to cart', {
                    type: 'error',
                });
            } else {
                setModalOpen(false);
                const lastAddedItem =
                    sampleFetcher.data?.addToShoppingCart.LastAddedItems[0];
                if (lastAddedItem) {
                    setLastItemAdded({
                        ...lastAddedItem,
                    });
                }
            }
        }
    }, [sampleFetcher, toast]);

    return (
        <div>
            <PurchaseButtonGroup onItemAdded={setLastItemAdded} />
            {mobile && whereToBuyEnabled && (
                <WhereToBuyButton onClick={onClickWhereToBuy} />
            )}
            {mobile && sampleCanPurchase && hasSampleShippingOptions && (
                <Flex
                    gap="space-20"
                    className={css(S.SaveShareContainer)}
                    hAlign="center"
                    vAlign="start"
                >
                    {sampleCanPurchase && hasSampleShippingOptions && (
                        <Button
                            size={16}
                            color="dark"
                            data-testid="order-sample-free"
                            className={css(S.LinkButton)}
                            onClick={() => {
                                tracker.log('OrderSampleSelect', {
                                    url: new URL(window.location.href),
                                    targetTracker: 'archiproTracker',
                                    data: {
                                        ExtraData:
                                            selectedVariant?.ID || product.ID,
                                    },
                                });
                                setModalOpen(true);
                            }}
                            iconBefore={<CustomShoppingCart />}
                            variables={{
                                iconPadding: 0,
                                buttonPadding: 0,
                            }}
                            variant={'text'}
                            text={true}
                        >
                            Order Sample ({samplePrice})
                        </Button>
                    )}
                </Flex>
            )}
            {sampleCanPurchase && !mobile && hasSampleShippingOptions && (
                <div className={css(S.SampleRow)}>
                    <span
                        onClick={() => {
                            tracker.log('OrderSampleSelect', {
                                url: new URL(window.location.href),
                                targetTracker: 'archiproTracker',
                                data: {
                                    ExtraData:
                                        selectedVariant?.ID || product.ID,
                                },
                            });
                            setModalOpen(true);
                        }}
                    >
                        Order Sample ({samplePrice})
                    </span>
                </div>
            )}
            <ShippingPopup
                open={modalOpen}
                onCancel={() => setModalOpen(false)}
                onConfirm={onSampleShippingConfirm}
                isSample={true}
                confirmButtonText={'Add to cart'}
                loading={sampleFetcher.state !== 'idle'}
            />
            {mobile && (
                <LastAddedPopup
                    open={Boolean(cartPopupOpen && lastItemAdded)}
                    item={lastItemAdded}
                    onOpenChange={(_, props) => {
                        if (props && !props.open) {
                            onCartPopupChange(false);
                        }
                    }}
                />
            )}
        </div>
    );
};

export default QuantityGroup;
